/**
 * Operator-managed API keys for https://api.peptideclients.com/v1/* (or
 * the Supabase Edge Function fallback). Wraps the SECURITY DEFINER RPCs
 * `core.rpc_create_api_key` / `core.rpc_revoke_api_key` /
 * `core.rpc_list_api_keys`.
 *
 * The plaintext key is returned ONCE at create time and never again. UI
 * shows it in a one-time modal; we intentionally don't persist it on
 * the client.
 *
 * Cast pattern: `(supabase() as any).rpc(...)` because the generated
 * Database types don't yet know about these RPCs (codegen runs at deploy).
 */
import { supabase } from '../client';

export type ApiKeyScope = 'read_only' | 'read_write';

export interface ApiKeyRow {
  id: string;
  name: string;
  key_prefix: string;
  scope: ApiKeyScope;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

export interface ListApiKeysInput {
  orgId: string;
  cursor?: { at: string; id: string } | null;
  limit?: number;
}

export async function listApiKeys(
  input: ListApiKeysInput,
): Promise<{ rows: ApiKeyRow[]; nextCursor: { at: string; id: string } | null }> {
  const limit = Math.max(1, Math.min(input.limit ?? 25, 100));
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_list_api_keys', {
      p_org_id: input.orgId,
      p_cursor_at: input.cursor?.at ?? null,
      p_cursor_id: input.cursor?.id ?? null,
      p_limit: limit,
    });
  if (error) throw error;
  const rows = ((data ?? []) as unknown) as ApiKeyRow[];
  const last = rows[rows.length - 1];
  return {
    rows,
    nextCursor:
      rows.length >= limit && last ? { at: last.created_at, id: last.id } : null,
  };
}

export interface CreateApiKeyInput {
  orgId: string;
  name: string;
  scope?: ApiKeyScope;
  expiresInDays?: number | null;
}

export async function createApiKey(
  input: CreateApiKeyInput,
): Promise<{ id: string; key_plain: string; key_prefix: string }> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_create_api_key', {
      p_org_id: input.orgId,
      p_name: input.name,
      p_scope: input.scope ?? 'read_only',
      p_expires_in_days: input.expiresInDays ?? null,
    });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { id: string; key_plain: string; key_prefix: string }
    | undefined;
  if (!row) throw new Error('createApiKey: no row returned');
  return row;
}

export async function revokeApiKey(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_revoke_api_key', { p_id: id });
  if (error) throw error;
}
